import type { ArticleBlock } from "@/content/types";

type Guide = {
  slug: string;
  title: string;
  description: string;
  authorSlug: string;
  reviewerSlug: string;
  updatedAt: string;
  publishedLabel: string;
  readingMinutes: number;
  blocks: ArticleBlock[];
  faq: { q: string; a: string }[];
};

/** Losse gidspagina: /online-casino-zonder-bkr/ */
export const guide: Guide = {
  "slug": "online-casino-zonder-bkr",
  "title": "Online casino zonder BKR: zo werkt spelen zonder kredietcheck",
  "description": "Beste online casino zonder BKR 2026: geen enkel legaal casino doet een BKR-toets. Lees wat ze wel controleren, welke betaalmethoden je gebruikt en waarom krediet niet kan.",
  "authorSlug": "robert-bemelmans",
  "reviewerSlug": "wietse-van-koppen",
  updatedAt: "4 november 2026",
  "publishedLabel": "1 november 2026",
  "readingMinutes": 22,
  "blocks": [
    {
      "type": "p",
      "text": "Zoek je een online casino zonder BKR? Dan is er goed nieuws. Geen enkel legaal online casino in Nederland kijkt in het BKR. Je hoeft bij het aanmelden geen kredietcheck te doen. Een BKR-registratie houdt je dus niet tegen om een account te openen."
    },
    {
      "type": "p",
      "text": "Toch zit er meer achter deze zoekterm. Veel mensen die zoeken op casino zonder BKR willen weten of een registratie problemen geeft. Anderen willen weten of ze kunnen spelen met geld dat ze lenen. Op die tweede vraag is het antwoord duidelijk nee. In deze gids leggen we alles stap voor stap uit."
    },
    {
      "type": "p",
      "text": "We controleerden in november 2026 bij alle aanbieders uit onze vergelijking wat er bij registratie wordt gevraagd. We keken welke gegevens ze opvragen, welke checks ze doen en welke betaalmethoden in de kassa staan. Zo weet je precies wat je kunt verwachten."
    },
    {
      "type": "callout",
      "title": "Kort antwoord",
      "text": "Legale online casino's in Nederland doen geen BKR-toetsing. Ze controleren wel je leeftijd, identiteit en Cruks. Spelen met geleend geld kan niet: creditcards en achteraf betalen zijn bij legale aanbieders niet toegestaan. Je stort altijd met je eigen geld, bijvoorbeeld via iDEAL."
    },
    {
      "type": "h2",
      "text": "Online casino zonder BKR: wat betekent het precies?"
    },
    {
      "type": "p",
      "text": "BKR staat voor Bureau Krediet Registratie. Het BKR houdt bij welke leningen en kredieten mensen in Nederland hebben. Denk aan een persoonlijke lening, een rood staan-limiet, een creditcard of een telefoonabonnement met een dure telefoon."
    },
    {
      "type": "p",
      "text": "Banken en kredietverstrekkers kijken in het BKR voordat ze je geld lenen. Zo willen ze voorkomen dat je te veel schulden opbouwt. Heb je een achterstand gehad, dan staat daar een zogenoemde codering bij."
    },
    {
      "type": "p",
      "text": "Een online casino leent je geen geld. Daarom heeft een casino geen reden om in het BKR te kijken. Het mag dat ook niet zomaar. Alleen partijen die zelf krediet verstrekken, krijgen toegang tot het register."
    },
    {
      "type": "p",
      "text": "Een online casino zonder BKR is dus eigenlijk elk legaal online casino. De term klinkt als iets bijzonders, maar het is gewoon de standaard."
    },
    {
      "type": "table",
      "caption": "Kassacheck BKR bij registratie (november 2026)",
      "head": [
        "Vraag",
        "Uitkomst"
      ],
      "rows": [
        [
          "BKR-toetsing bij aanmelden",
          "Bij geen van de vergeleken aanbieders"
        ],
        [
          "Vraag naar je inkomen bij aanmelden",
          "Bij geen van de vergeleken aanbieders"
        ],
        [
          "Leeftijdscheck 18+",
          "Bij alle vergeleken aanbieders"
        ],
        [
          "Cruks-controle",
          "Bij alle vergeleken aanbieders"
        ],
        [
          "Creditcard in de kassa",
          "Bij geen van de vergeleken aanbieders"
        ],
        [
          "Achteraf betalen in de kassa",
          "Bij geen van de vergeleken aanbieders"
        ]
      ]
    },
    {
      "type": "h2",
      "text": "Waarom kijkt een casino niet in het BKR?"
    },
    {
      "type": "p",
      "text": "Er zijn drie redenen. De eerste is simpel: een casino verstrekt geen krediet. Je betaalt vooraf. Pas als het geld op je saldo staat, kun je spelen."
    },
    {
      "type": "p",
      "text": "De tweede reden is privacy. Het BKR is alleen open voor deelnemers die zelf geld uitlenen. Een casino is geen deelnemer. Het kan dus niet zien of jij een lening hebt."
    },
    {
      "type": "p",
      "text": "De derde reden is de wet. Nederlandse casino's moeten zelf controleren of je verantwoord speelt. Dat doen ze niet via het BKR, maar met eigen checks. Bijvoorbeeld door te kijken naar hoeveel je stort en hoe vaak je speelt."
    },
    {
      "type": "h3",
      "text": "Wat controleert een casino dan wel?"
    },
    {
      "type": "ul",
      "items": [
        "Je naam, geboortedatum en adres.",
        "Of je 18 jaar of ouder bent.",
        "Of je in Cruks staat, het register voor zelfuitsluiting.",
        "Of de bankrekening op jouw naam staat.",
        "Bij hogere bedragen: waar je geld vandaan komt."
      ]
    },
    {
      "type": "p",
      "text": "Die laatste check verrast soms. Stort je veel geld in korte tijd, dan kan een casino vragen om een loonstrook of bankafschrift. Dat heet een check op herkomst van middelen. Het is geen BKR-toets, maar het lijkt er wel op. Het doel is jou en het casino te beschermen."
    },
    {
      "type": "h2",
      "text": "Kun je met een BKR-registratie spelen?"
    },
    {
      "type": "p",
      "text": "Ja. Een BKR-codering is geen reden om je te weigeren. Je kunt gewoon een account openen bij een legaal online casino. Je naam staat dan wel in het BKR, maar dat ziet het casino niet."
    },
    {
      "type": "p",
      "text": "Wel is het goed om even eerlijk naar jezelf te kijken. Een BKR-codering betekent dat je ooit een betalingsachterstand had. Gaat het nu goed met je geldzaken? Dan is er niets aan de hand. Heb je nog schulden lopen? Dan is gokken geen slimme keuze. Het geld dat je inzet, ben je meestal kwijt."
    },
    {
      "type": "callout",
      "title": "Tip van de redactie",
      "text": "Stel bij je eerste storting meteen een stortlimiet in. Dat kan bij elk legaal casino in je account. Zo bepaal jij vooraf hoeveel je maximaal per week of maand uitgeeft."
    },
    {
      "type": "h2",
      "text": "Online casino zonder BKR-toets: spelen met geleend geld kan niet"
    },
    {
      "type": "p",
      "text": "Sommige mensen zoeken een casino zonder BKR omdat ze willen spelen met krediet. Dat gaat niet bij legale aanbieders. De Nederlandse regels zijn hier streng."
    },
    {
      "type": "p",
      "text": "Creditcards zijn niet toegestaan in Nederlandse online casino's. Visa en Mastercard werken alleen als debitkaart, dus met geld dat al op je rekening staat. Ook achteraf betalen met Klarna of Riverty kan niet. Klarna mag alleen als directe betaling, waarbij het geld meteen van je rekening gaat."
    },
    {
      "type": "p",
      "text": "Rood staan kan in theorie wel. Als je bank je laat rood staan en je stort met iDEAL, dan gebruik je eigenlijk geleend geld. Dat raden we sterk af. Rente op rood staan is hoog en verlies je het geld, dan blijft de schuld staan."
    },
    {
      "type": "table",
      "caption": "Welke betaalmethoden gebruiken krediet?",
      "head": [
        "Methode",
        "In legaal NL casino",
        "Krediet mogelijk?"
      ],
      "rows": [
        [
          "iDEAL",
          "Ja",
          "Alleen als je rood staat"
        ],
        [
          "Trustly",
          "Ja",
          "Alleen als je rood staat"
        ],
        [
          "Debitkaart (Visa/Mastercard)",
          "Ja",
          "Nee"
        ],
        [
          "Creditcard",
          "Nee",
          "Ja, daarom verboden"
        ],
        [
          "Klarna achteraf betalen",
          "Nee",
          "Ja, daarom verboden"
        ],
        [
          "AfterPay / Riverty",
          "Nee",
          "Ja, daarom verboden"
        ],
        [
          "Paysafecard",
          "Ja",
          "Nee, prepaid"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Wil je echt alleen met eigen geld spelen? Kies dan een prepaid methode. Met Paysafecard koop je vooraf een tegoed. Meer dan dat bedrag kun je niet storten. Lees er meer over op onze pagina over Paysafecard."
    },
    {
      "type": "h2",
      "text": "Casino zonder BKR en zonder Cruks: waarom we dat niet aanraden"
    },
    {
      "type": "p",
      "text": "Online zie je vaak sites die beide beloven: geen BKR en geen Cruks. Dat zijn casino's zonder Nederlandse vergunning. Ze staan niet in het register van de Kansspelautoriteit."
    },
    {
      "type": "p",
      "text": "Bij die sites heb je geen bescherming. Er is geen klachtencommissie. Je winst wordt niet gegarandeerd uitbetaald. En als je ooit Cruks hebt aangevraagd, dan had je daar een goede reden voor."
    },
    {
      "type": "p",
      "text": "Daarnaast zijn de betaalmethoden daar vaak riskanter. Je stort met crypto of een creditcard. Bij problemen krijg je dat geld niet terug. Een chargeback lukt dan bijna nooit."
    },
    {
      "type": "p",
      "text": "Wij vergelijken daarom alleen aanbieders met een Nederlandse vergunning. Daar doen ze ook geen BKR-toets. Je mist dus niets."
    },
    {
      "type": "h2",
      "text": "Zo speel je bij een legaal casino zonder BKR-check"
    },
    {
      "type": "p",
      "text": "Een account openen kost een paar minuten. Hieronder staan de stappen die we bij elke aanbieder doorliepen."
    },
    {
      "type": "ol",
      "items": [
        "Kies een aanbieder uit onze vergelijking bovenaan deze pagina.",
        "Vul je naam, geboortedatum, adres en BSN in. Het BSN is nodig voor de Cruks-check.",
        "Bevestig je identiteit. Dat kan met iDIN via je bank, of met een foto van je ID-bewijs.",
        "Stel je stortlimiet, inloglimiet en speeltijdlimiet in. Dit is verplicht.",
        "Doe een eerste storting met iDEAL of Trustly vanaf een rekening op jouw naam.",
        "Bewaar het bewijs van je storting. Dat helpt bij je eerste uitbetaling."
      ]
    },
    {
      "type": "p",
      "text": "Geen enkele stap vraagt om een BKR-check of een inkomensverklaring. Alleen bij grote bedragen kan later een extra vraag komen."
    },
    {
      "type": "h2",
      "text": "Betaalmethoden bij een online casino zonder BKR"
    },
    {
      "type": "p",
      "text": "Omdat je met eigen geld speelt, maakt de betaalmethode het verschil. Elke methode heeft eigen voordelen. Hieronder staan de meest gebruikte opties in Nederland."
    },
    {
      "type": "table",
      "caption": "Betaalmethoden vergeleken voor spelen zonder krediet",
      "head": [
        "Methode",
        "Storting",
        "Uitbetaling",
        "Eigen geld?"
      ],
      "rows": [
        [
          "iDEAL",
          "Direct",
          "Via bankoverschrijving",
          "Ja, van je rekening"
        ],
        [
          "Trustly",
          "Direct",
          "Vaak snel naar je rekening",
          "Ja, van je rekening"
        ],
        [
          "Paysafecard",
          "Direct",
          "Niet mogelijk",
          "Ja, prepaid tegoed"
        ],
        [
          "Debitkaart",
          "Direct",
          "Soms naar de kaart",
          "Ja, van je rekening"
        ],
        [
          "Skrill / Neteller",
          "Direct",
          "Naar je e-wallet",
          "Ja, tegoed in wallet"
        ],
        [
          "Apple Pay",
          "Direct",
          "Meestal via bank",
          "Ja, via debitkaart"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Voor de meeste spelers is iDEAL de beste keuze. Het is snel, gratis en bekend. Bovendien bevestigt je bank meteen dat de rekening van jou is. Dat versnelt je eerste opname."
    },
    {
      "type": "p",
      "text": "Wil je je budget strak houden? Kijk dan ook naar casino's met een lage storting. Op onze pagina over 10 euro deposit casino's lees je welke aanbieders kleine bedragen accepteren."
    },
    {
      "type": "h2",
      "text": "Uitbetalen zonder BKR: hoe werkt dat?"
    },
    {
      "type": "p",
      "text": "Uitbetalen heeft niets met het BKR te maken. Je winst gaat naar de rekening op jouw naam. Het BKR ziet dat niet en het telt niet mee als inkomen bij een lening."
    },
    {
      "type": "p",
      "text": "Wel kan een casino bij je eerste opname extra checks doen. Denk aan een foto van je ID of een bankafschrift. Dat is normaal. Het is geen kredietcheck, maar een identiteitscheck."
    },
    {
      "type": "table",
      "caption": "Wat je kunt verwachten bij je eerste uitbetaling",
      "head": [
        "Onderdeel",
        "Bij legale aanbieders"
      ],
      "rows": [
        [
          "BKR-check",
          "Nee"
        ],
        [
          "Identiteitscheck",
          "Ja, als dat nog niet gedaan is"
        ],
        [
          "Rekening op eigen naam",
          "Verplicht"
        ],
        [
          "Verwerkingstijd",
          "Van binnen het uur tot enkele werkdagen"
        ],
        [
          "Kosten",
          "Bij de meeste aanbieders geen"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Wil je weten waar je het snelst je geld krijgt? Lees dan onze gids over online casino's met een snelle uitbetaling."
    },
    {
      "type": "h2",
      "text": "Heeft gokken invloed op je BKR-registratie?"
    },
    {
      "type": "p",
      "text": "Gokken zelf komt niet in het BKR. Een storting of een verlies wordt nergens geregistreerd als schuld. Ook een grote winst komt er niet in."
    },
    {
      "type": "p",
      "text": "Het BKR kan wel indirect een rol spelen. Leen je geld om te gokken en betaal je dat niet terug? Dan komt die achterstand wel in het BKR. Het gaat dan om de lening, niet om het gokken."
    },
    {
      "type": "p",
      "text": "Bij een hypotheekaanvraag kijkt een bank soms naar je bankafschriften. Zien ze veel stortingen naar casino's, dan kunnen ze vragen stellen. Dat staat los van het BKR, maar het is goed om te weten."
    },
    {
      "type": "table",
      "caption": "Wat komt wel en niet in het BKR?",
      "head": [
        "Situatie",
        "In het BKR?"
      ],
      "rows": [
        [
          "Storting in een online casino",
          "Nee"
        ],
        [
          "Verlies in een online casino",
          "Nee"
        ],
        [
          "Winst uit een online casino",
          "Nee"
        ],
        [
          "Creditcard of lening",
          "Ja"
        ],
        [
          "Rood staan boven een bepaalde limiet",
          "Ja"
        ],
        [
          "Achterstand op een lening",
          "Ja, met codering"
        ]
      ]
    },
    {
      "type": "h2",
      "text": "BKR-codering en schulden: waar vind je hulp?"
    },
    {
      "type": "p",
      "text": "Heb je schulden en merk je dat gokken een rol speelt? Je bent niet de enige. Er is gratis en anonieme hulp beschikbaar."
    },
    {
      "type": "ul",
      "items": [
        "Loket Kansspel: gratis en anoniem advies over gokproblemen.",
        "Je gemeente: hulp bij schulden via de schuldhulpverlening.",
        "Geldfit: een online test en advies over je geldzaken.",
        "Cruks: je kunt jezelf uitsluiten van alle legale kansspelen."
      ]
    },
    {
      "type": "p",
      "text": "Kies je voor Cruks, dan sta je minimaal zes maanden uitgesloten. Alle legale casino's controleren dat. Lees meer op onze pagina over verantwoord spelen."
    },
    {
      "type": "callout",
      "title": "Signalen om op te letten",
      "text": "Je leent geld om te gokken. Je probeert verlies terug te winnen. Je verzwijgt hoeveel je speelt. Herken je één van deze punten? Neem dan contact op met Loket Kansspel."
    },
    {
      "type": "h2",
      "text": "Zo herken je een betrouwbaar casino zonder BKR"
    },
    {
      "type": "p",
      "text": "Elk legaal casino doet geen BKR-check. Maar hoe weet je of een site legaal is? Dat kun je zelf nagaan in een minuut."
    },
    {
      "type": "ol",
      "items": [
        "Zoek onderaan de site naar de bedrijfsnaam en het vergunningsnummer.",
        "Open het register van vergunninghouders bij de Kansspelautoriteit.",
        "Kijk of de naam en de website daar letterlijk in staan.",
        "Let op het 18+-logo en de link naar Loket Kansspel."
      ]
    },
    {
      "type": "p",
      "text": "Staat de site niet in het register? Dan heeft hij geen Nederlandse vergunning. Ga dan niet verder. Meer over dit verschil lees je in onze gids over online casino's in het buitenland."
    },
    {
      "type": "h2",
      "text": "Veelgemaakte fouten bij spelen zonder BKR"
    },
    {
      "type": "p",
      "text": "We zien in vragen van lezers steeds dezelfde fouten terug. Hieronder de vijf belangrijkste."
    },
    {
      "type": "ul",
      "items": [
        "Denken dat een casino zonder BKR een speciaal type casino is. Elk legaal casino doet geen BKR-check.",
        "Storten vanaf een rekening die rood staat. Zo speel je met geleend geld.",
        "Een creditcard willen gebruiken. Dat is in Nederland niet toegestaan.",
        "Een site kiezen omdat die geen Cruks controleert. Daar heb je geen bescherming.",
        "Geen limiet instellen. Zonder limiet verlies je sneller het overzicht."
      ]
    },
    {
      "type": "h2",
      "text": "BKR-coderingen kort uitgelegd"
    },
    {
      "type": "p",
      "text": "Wie een achterstand heeft, krijgt in het BKR een codering. Er zijn verschillende soorten. Ze zeggen iets over hoe ernstig de achterstand was en of die is opgelost."
    },
    {
      "type": "table",
      "caption": "De belangrijkste BKR-coderingen",
      "head": [
        "Codering",
        "Betekenis"
      ],
      "rows": [
        [
          "A",
          "Achterstand van meer dan twee maanden"
        ],
        [
          "H",
          "Achterstand is volledig ingelopen"
        ],
        [
          "2",
          "Schuld is deels via een regeling betaald"
        ],
        [
          "3",
          "Kredietverstrekker heeft de schuld afgeboekt"
        ],
        [
          "R",
          "Schuldregeling via een officiële instantie"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Een codering blijft vijf jaar staan nadat de achterstand is opgelost. Daarna verdwijnt hij vanzelf. Je kunt je eigen registratie gratis inzien op de site van het BKR."
    },
    {
      "type": "p",
      "text": "Voor het casino maakt geen van deze coderingen uit. Het casino ziet ze niet. Voor jezelf maken ze wel uit. Een codering A of R is een duidelijk signaal om nu niet te gokken."
    },
    {
      "type": "h2",
      "text": "Wat zegt de wet over krediet en online gokken?"
    },
    {
      "type": "p",
      "text": "Sinds oktober 2021 is online gokken legaal in Nederland. Dat staat in de Wet Kansspelen op afstand, kortweg de Wet Koa. De wet bevat duidelijke regels over geld en krediet."
    },
    {
      "type": "ul",
      "items": [
        "Een casino mag je geen krediet geven.",
        "Een casino mag geen creditcard accepteren.",
        "Je moet vooraf een stortlimiet instellen.",
        "Het casino moet ingrijpen als je speelgedrag risicovol wordt.",
        "Voor spelers tot 24 jaar gelden lagere standaardlimieten."
      ]
    },
    {
      "type": "p",
      "text": "Die regels zijn er om spelers te beschermen. Ze zorgen ervoor dat je niet meer kunt verliezen dan je hebt. Een BKR-check is daarom niet nodig. De bescherming zit al in de regels zelf."
    },
    {
      "type": "p",
      "text": "In 2024 en 2025 zijn de regels nog strenger geworden. Casino's moeten nu bij lagere bedragen al vragen of je het geld kunt missen. Voor jonge spelers gelden harde maandlimieten. Kijkt een casino bij jou naar je financiële situatie? Dan volgt het gewoon de wet."
    },
    {
      "type": "h2",
      "text": "Voorbeeld: een budgetplan zonder krediet"
    },
    {
      "type": "p",
      "text": "Hoe speel je verstandig met alleen eigen geld? Hieronder een voorbeeld van een speler die per maand een vast bedrag opzij zet voor ontspanning."
    },
    {
      "type": "table",
      "caption": "Voorbeeld van een maandbudget voor casinospel",
      "head": [
        "Stap",
        "Wat je doet",
        "Voorbeeld"
      ],
      "rows": [
        [
          "1",
          "Bepaal je vrije budget na vaste lasten",
          "€ 50 per maand"
        ],
        [
          "2",
          "Stel een stortlimiet in bij het casino",
          "€ 50 per maand"
        ],
        [
          "3",
          "Kies een methode zonder krediet",
          "iDEAL of Paysafecard"
        ],
        [
          "4",
          "Verdeel over sessies",
          "Vier keer € 12,50"
        ],
        [
          "5",
          "Stop bij verlies van je sessiebudget",
          "Niet bijstorten"
        ],
        [
          "6",
          "Neem winst direct op",
          "Naar je eigen rekening"
        ]
      ]
    },
    {
      "type": "p",
      "text": "Het idee is simpel. Je bepaalt vooraf wat je wilt uitgeven. Dat bedrag is weg, net als een avondje bioscoop. Komt er winst, dan is dat een meevaller. Zo blijft het leuk en loop je geen risico op schulden."
    },
    {
      "type": "h2",
      "text": "Mythes over casino's en het BKR"
    },
    {
      "type": "h3",
      "text": "Mythe 1: met een BKR-registratie mag je niet gokken"
    },
    {
      "type": "p",
      "text": "Niet waar. Er is geen regel die spelers met een BKR-codering weert. Alleen Cruks kan je uitsluiten, en dat kies je zelf of laat je op verzoek van naasten doen."
    },
    {
      "type": "h3",
      "text": "Mythe 2: een casino zonder BKR is een illegaal casino"
    },
    {
      "type": "p",
      "text": "Ook niet waar. Elk legaal casino werkt zonder BKR-check. Het verschil met illegale sites zit in de vergunning en de Cruks-controle, niet in het BKR."
    },
    {
      "type": "h3",
      "text": "Mythe 3: winst uit een casino telt mee voor een hypotheek"
    },
    {
      "type": "p",
      "text": "Nee. Een bank ziet casinowinst niet als vast inkomen. Het telt niet mee voor je leencapaciteit. Veel stortingen naar casino's kunnen juist vragen oproepen."
    },
    {
      "type": "h3",
      "text": "Mythe 4: met een prepaidkaart kan je ongemerkt spelen"
    },
    {
      "type": "p",
      "text": "Een prepaid voucher zoals Paysafecard helpt om je budget te bewaken. Maar je moet nog steeds een account op eigen naam hebben. Je leeftijd en Cruks worden altijd gecontroleerd."
    },
    {
      "type": "h2",
      "text": "Casino zonder BKR voor jongvolwassenen"
    },
    {
      "type": "p",
      "text": "Ben je tussen de 18 en 24 jaar? Dan gelden er extra regels. Een casino moet voor jou lagere standaardlimieten instellen. Ook krijg je sneller een gesprek als je veel stort."
    },
    {
      "type": "p",
      "text": "Jongvolwassenen hebben vaak nog geen BKR-registratie. Een studielening van DUO komt ook niet in het BKR. Toch kan een studieschuld wel meewegen als je later een hypotheek wilt. Speel daarom niet met geld dat eigenlijk voor je studie of vaste lasten is."
    },
    {
      "type": "p",
      "text": "Wil je meer weten over spelen met kleine bedragen? Lees onze gids over 5 euro deposit casino's."
    },
    {
      "type": "h2",
      "text": "Conclusie: online casino zonder BKR"
    },
    {
      "type": "p",
      "text": "Een online casino zonder BKR is geen uitzondering. Het is de regel. Geen enkel legaal casino in Nederland doet een kredietcheck. Een BKR-registratie houdt je dus niet tegen."
    },
    {
      "type": "p",
      "text": "Spelen met geleend geld kan niet en dat is maar goed ook. Je stort altijd met je eigen geld. Kies een betaalmethode die daarbij past, zoals iDEAL, Trustly of een prepaid voucher."
    },
    {
      "type": "p",
      "text": "Stel altijd een limiet in en speel alleen met geld dat je kunt missen. Gaat het mis? Er is gratis hulp. Zo blijft spelen wat het hoort te zijn: ontspanning."
    },
    {
      "type": "h2",
      "text": "Hoe wij deze pagina onderhouden"
    },
    {
      "type": "p",
      "text": "We controleren de kassa en het registratieproces van de vergeleken aanbieders elke maand. Veranderen regels rond krediet, Cruks of betaalmethoden, dan passen we deze pagina aan. Zie je een fout? Laat het ons weten via de contactpagina."
    }
  ],
  "faq": [
    {
      "q": "Is er een online casino zonder BKR?",
      "a": "Ja, eigenlijk elk legaal online casino in Nederland. Casino's verstrekken geen krediet en hebben geen toegang tot het BKR. Je kunt dus spelen met een BKR-registratie."
    },
    {
      "q": "Kijkt een casino in het BKR bij registratie?",
      "a": "Nee. Een casino controleert je leeftijd, identiteit en Cruks, maar niet het BKR. Het BKR is alleen open voor partijen die zelf geld uitlenen."
    },
    {
      "q": "Kan ik met een BKR-codering een casino-account openen?",
      "a": "Ja. Een BKR-codering is geen reden voor weigering. Kijk wel eerlijk naar je financiële situatie voordat je speelt."
    },
    {
      "q": "Kan ik in een casino spelen met geleend geld?",
      "a": "Nee, niet via het casino. Creditcards en achteraf betalen zijn niet toegestaan bij legale aanbieders. Je stort met je eigen geld, bijvoorbeeld via iDEAL."
    },
    {
      "q": "Komt gokken in het BKR?",
      "a": "Nee. Stortingen, verlies en winst worden niet in het BKR geregistreerd. Alleen een lening die je niet terugbetaalt, kan daar terechtkomen."
    },
    {
      "q": "Mag ik met een creditcard storten in een Nederlands casino?",
      "a": "Nee. Sinds de Wet Koa zijn creditcards verboden bij Nederlandse online casino's. Visa en Mastercard werken alleen als debitkaart."
    },
    {
      "q": "Kan ik met Klarna achteraf betalen in een casino?",
      "a": "Nee. Klarna is alleen beschikbaar als directe betaling. Het geld gaat dan meteen van je rekening."
    },
    {
      "q": "Vraagt een casino om mijn inkomen?",
      "a": "Niet bij registratie. Bij hogere stortingen kan een casino wel vragen waar je geld vandaan komt, bijvoorbeeld met een loonstrook of bankafschrift."
    },
    {
      "q": "Wat is het verschil tussen BKR en Cruks?",
      "a": "Het BKR registreert leningen en schulden. Cruks is het register waarin je jezelf kunt uitsluiten van kansspelen. Casino's controleren alleen Cruks."
    },
    {
      "q": "Waar vind ik hulp bij gokschulden?",
      "a": "Bij Loket Kansspel, gratis en anoniem. Voor schulden kun je ook terecht bij de schuldhulpverlening van je gemeente."
    }
  ]
};
